import type { ILanguageJson } from '../types/languageJson';
import { getRuntimeLocale } from './localeText';
import {
  getRuntimeLocaleForShare,
  RUNTIME_LANG_QUERY_KEY,
} from './runtimeLocale';
import {
  LANGUAGE_JSON_KEYS,
  normalizeLanguageJson,
  resolveLanguageJsonText,
} from './languageJson';

const hasText = (value?: string | null): boolean => {
  return typeof value === 'string' && value.trim().length > 0;
};

const getGlobalLanguageJson = (): ILanguageJson => {
  return normalizeLanguageJson(window.configDetail?.language_json);
};

const getPageLanguageJson = (): ILanguageJson => {
  return normalizeLanguageJson(window.pageDetail?.language_json);
};

/**
 * 分享链路里的语言需要尽量“跟着当前用户正在看的语言走”：
 * - 优先当前 URL 中显式的 lang
 * - 其次同 tab 内 session 里记住的 lang
 * - 最后再回退到本地设置或默认语言
 */
export const appendShareLangToUrl = (url: string = ''): string => {
  if (!url) {
    return '';
  }

  const runtimeLocale = getRuntimeLocaleForShare() || getRuntimeLocale();
  if (!runtimeLocale) {
    return url;
  }

  try {
    if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) {
      const target = new URL(url);
      target.searchParams.set(RUNTIME_LANG_QUERY_KEY, runtimeLocale);
      return target.toString();
    }

    const [withoutHash, hash = ''] = url.split('#');
    const [pathname, query = ''] = withoutHash.split('?');
    const params = new URLSearchParams(query);
    params.set(RUNTIME_LANG_QUERY_KEY, runtimeLocale);
    const nextQuery = params.toString();
    return `${pathname}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`;
  } catch (error) {
    return url;
  }
};

export const getLocalizedGalleryPrefix = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.gallery?.prefix,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.prefix,
    locale
  );
};

export const getLocalizedPageNavigator = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.navigator,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.navigator,
    locale,
    window.pageDetail?.navigator_i18n
  );
};

export const getLocalizedGlobalShareTitle = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.configDetail?.h5_share_title,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareTitle,
    locale
  );
};

export const getLocalizedGlobalShareText = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.configDetail?.h5_share_text,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareText,
    locale
  );
};

export const getLocalizedPageShareTitle = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.h5_share_title,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareTitle,
    locale
  );
};

export const getLocalizedPageShareText = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.h5_share_text,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareText,
    locale
  );
};

export const hasPageShareOverride = (locale: string = getRuntimeLocale()): boolean => {
  return Boolean(
    window.pageDetail?.h5_share_image &&
      hasText(getLocalizedPageShareTitle(locale)) &&
      hasText(getLocalizedPageShareText(locale))
  );
};

export const getEffectiveShareTitle = (locale: string = getRuntimeLocale()): string => {
  if (hasPageShareOverride(locale)) {
    return getLocalizedPageShareTitle(locale);
  }

  if (
    window.dataDetail &&
    window.dataDetail.name &&
    window.dataDetail.id !== window.gallery.id
  ) {
    return `${getLocalizedGalleryPrefix(locale)}${window.dataDetail.name}${
      window.gallery?.name ? `-${window.gallery.name}` : ''
    }`;
  }

  const globalTitle = getLocalizedGlobalShareTitle(locale);
  if (hasText(globalTitle)) {
    return `${getLocalizedGalleryPrefix(locale)}${globalTitle}`;
  }

  return `${getLocalizedGalleryPrefix(locale)}${window.gallery?.name || ''}`;
};

export const getEffectiveShareText = (locale: string = getRuntimeLocale()): string => {
  if (hasPageShareOverride(locale)) {
    return getLocalizedPageShareText(locale);
  }

  return getLocalizedGlobalShareText(locale);
};

export const getEffectiveShareImageKey = (): string => {
  if (hasPageShareOverride()) {
    return window.pageDetail?.h5_share_image || '';
  }

  return window.configDetail?.h5_share_image || '';
};